import { useEffect, useRef, useState } from 'react';
import { useAxios } from '../../Helpers/AxiosHelper';
import { AutoComplete, AutoCompleteChangeEvent, AutoCompleteCompleteEvent } from '../../Common/Imports';
import { ApiResponse } from '../../Models/Classes';
import { useTranslation } from 'react-i18next';

/**
 * Hotel name lookup for the operative search mask.
 *
 * Suggestions come from the server, which draws them from the property registry and
 * from the names carried on reservations, so a hotel can be found under either of the
 * two names the list displays for it.
 *
 * The operator is not obliged to pick from the suggestions: whatever is typed is passed
 * through as the filter value. That keeps a name typed from memory or carried in a
 * shared search working, and keeps the field usable if the lookup call fails.
 */

/** Shorter terms match almost everything, so the server declines to search for them. */
const MINIMUM_SEARCH_LENGTH = 3;

/** One request per pause in typing rather than one per keystroke. */
const DEBOUNCE_MS = 300;

interface HotelNameLookupResult {
    names: string[];
    hasMore: boolean;
}

interface DDHotelNamesProps {
    value: string;
    onChange: (value: string) => void;
}

export const DDHotelNames: React.FC<DDHotelNamesProps> = ({ value, onChange }) => {
    const [suggestions, setSuggestions] = useState<string[]>([]);
    const [hasMore, setHasMore] = useState(false);
    const AxiosHelper = useAxios();
    const { t } = useTranslation();
    const debounceRef = useRef<number | undefined>(undefined);

    useEffect(() => {
        return () => {
            if (debounceRef.current !== undefined) {
                window.clearTimeout(debounceRef.current);
            }
        };
    }, []);

    const Search = (event: AutoCompleteCompleteEvent) => {
        const query = event.query?.trim() ?? '';

        if (debounceRef.current !== undefined) {
            window.clearTimeout(debounceRef.current);
        }

        if (query.length < MINIMUM_SEARCH_LENGTH) {
            setSuggestions([]);
            setHasMore(false);
            return;
        }

        debounceRef.current = window.setTimeout(async () => {
            try {
                const resp = await AxiosHelper.get<ApiResponse<HotelNameLookupResult>>(
                    `DropDown/HotelNames/${encodeURIComponent(query)}`
                );
                if (resp.data.code === 0) {
                    setSuggestions(resp.data.data.names ?? []);
                    setHasMore(resp.data.data.hasMore ?? false);
                }
            } catch (error) {
                // A failed lookup must not block the filter: the operator can still type
                // a name and search with it.
                console.error('Error fetching hotel names:', error);
                setSuggestions([]);
                setHasMore(false);
            }
        }, DEBOUNCE_MS);
    };

    const handleChange = (e: AutoCompleteChangeEvent) => {
        onChange((e.value as string) ?? '');
    };

    return (
        <>
            <AutoComplete
                value={value}
                suggestions={suggestions}
                completeMethod={Search}
                onChange={handleChange}
                delay={0}
                minLength={MINIMUM_SEARCH_LENGTH}
                className="w-100"
                inputClassName="w-100"
            />
            {hasMore && (
                <small className="text-muted d-block mt-1">
                    {t('Operative.Table.HotelNameMoreResults')}
                </small>
            )}
        </>
    );
};

export default DDHotelNames;
